import * as Blockly from 'blockly/core';
import { useBlockModalRgbLedStore } from '@/stores/useBlockModalRgbLedStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

export interface ColorData {
    r: number;
    g: number;
    b: number;
    duration: number;
    enabled: boolean;
}

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 90;
const SWATCH_SIZE = 24;
const SWATCH_RADIUS = 4;
const SWATCH_GAP = 6;
const PADDING = 8;
const DISABLED_FONT_SIZE = 13;

export class FieldColorSequence extends Blockly.Field<string> {
    private colors_: ColorData[];
    private swatchEls_: SVGElement[] = [];

    static override fromJson(options: Blockly.FieldConfig & { colors?: ColorData[] }): FieldColorSequence {
        return new FieldColorSequence(options.colors ?? []);
    }

    constructor(colors: ColorData[]) {
        super(JSON.stringify(colors));
        this.colors_ = colors;
        this.SERIALIZABLE = true;
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#e65100';
        const fillColour = this.colors_.length === 0 ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private rgbToHex_(r: number, g: number, b: number): string {
        return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
    }

    private calcWidth_(): number {
        if (this.colors_.length === 0) return FIELD_MIN_WIDTH;
        return PADDING + this.colors_.length * (SWATCH_SIZE + SWATCH_GAP) - SWATCH_GAP + PADDING;
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_) return;

        this.swatchEls_.forEach((el) => el.parentNode?.removeChild(el));
        this.swatchEls_ = [];

        if (this.colors_.length === 0) {
            const text = Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.TEXT,
                {
                    x: PADDING,
                    y: FIELD_HEIGHT / 2 + 6,
                    'font-size': '17px',
                    'font-weight': '500',
                    'font-family': 'Corporate Logo, sans-serif',
                    style: 'fill: white !important;',
                },
                this.fieldGroup_,
            ) as SVGTextElement;
            text.textContent = '設定した色';
            this.swatchEls_.push(text);
        } else {
            const swatchY = (FIELD_HEIGHT - SWATCH_SIZE) / 2;
            this.colors_.forEach((color, i) => {
                const x = PADDING + i * (SWATCH_SIZE + SWATCH_GAP);

                if (color.enabled) {
                    const rect = Blockly.utils.dom.createSvgElement(
                        Blockly.utils.Svg.RECT,
                        {
                            x,
                            y: swatchY,
                            width: SWATCH_SIZE,
                            height: SWATCH_SIZE,
                            rx: SWATCH_RADIUS,
                            ry: SWATCH_RADIUS,
                            style: `fill: ${this.rgbToHex_(
                                color.r,
                                color.g,
                                color.b,
                            )} !important; stroke: rgba(0,0,0,0.3); stroke-width: 1;`,
                        },
                        this.fieldGroup_,
                    );
                    this.swatchEls_.push(rect);
                } else {
                    const rect = Blockly.utils.dom.createSvgElement(
                        Blockly.utils.Svg.RECT,
                        {
                            x,
                            y: swatchY,
                            width: SWATCH_SIZE,
                            height: SWATCH_SIZE,
                            rx: SWATCH_RADIUS,
                            ry: SWATCH_RADIUS,
                            style: 'fill: rgba(0,0,0,0.3) !important; stroke: rgba(255,255,255,0.2); stroke-width: 1;',
                        },
                        this.fieldGroup_,
                    );
                    const text = Blockly.utils.dom.createSvgElement(
                        Blockly.utils.Svg.TEXT,
                        {
                            x: x + SWATCH_SIZE / 2,
                            y: swatchY + SWATCH_SIZE / 2 + DISABLED_FONT_SIZE / 2 - 2,
                            'font-size': `${DISABLED_FONT_SIZE}px`,
                            'text-anchor': 'middle',
                            style: 'fill: rgba(255,255,255,0.5) !important;',
                        },
                        this.fieldGroup_,
                    ) as SVGTextElement;
                    text.textContent = '消';
                    this.swatchEls_.push(rect, text);
                }
            });
        }

        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        this.applyStyle_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setColors(colors: ColorData[]): void {
        this.colors_ = colors;
        const json = JSON.stringify(colors);
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', json));
        }
    }

    public getColors(): ColorData[] {
        return this.colors_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            this.colors_ = JSON.parse(newValue) as ColorData[];
        } catch {
            this.colors_ = [];
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return JSON.stringify(this.colors_);
    }

    override getSize(): Blockly.utils.Size {
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalRgbLedStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef({ setColors: (colors) => this.setColors(colors), getColors: () => this.getColors() });
        openModal('rgb');
    }
}
